import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Button, Col, Input, Row, Table, Pagination as Paging } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/layout/PageHeader';
import { updateMultiModel } from '../../../lib/api/algorithm-api';
import { deleteModel } from '../../../lib/api/algorithm-api';
import { GetModelOverviewTableColumns } from './ModelOverviewTable';
import { isDocumentClassification, isMultiflow } from '../../../lib/util';
import { getAlgorithmValue } from '../../../lib/util/algorithm';
import { Model, Training, Pagination } from '../../../types';
import CreateClassificationModel from './CreateClassificationModel';
import CreateModel from './CreateModel';
import CreateMultiModel from './CreateMultiModel';
import { useGetAlgorithm, useGetModels } from '../../../lib/hooks';
import { algorithmMenuDescriptions } from '../../../lib/status-templates';
import { RenameModel } from './RenameModel';
import { RenameMultiModel } from './RenameMultiModel';
import { ErrorText } from '../../../components/text/ErrorText';
import { startTraining } from '../../../lib/api/training-api';

const defaultPaging = {
  pageSize: 10,
  page: 1,
};

type Props = {};

const ModelOverview = (props: Props) => {
  let history = useHistory();

  const { algorithmId } = useParams() as { algorithmId: string };

  const { algorithm } = useGetAlgorithm(algorithmId);
  const [createModel, setCreateModel] = useState<boolean>(false);
  const [renameModel, setRenameModel] = useState<Model | null>(null);
  const [paginationObject, setPaginationObject] = useState<Pagination>(defaultPaging);
  const [filter, setFilter] = useState<string>('');

  const { models, totalItemCount, loading, refetch, error } = useGetModels(algorithmId, paginationObject, filter);

  const onCreateModel = () => {
    setCreateModel(true);
  };

  const onClose = () => {
    setCreateModel(false);
  };

  const onCreate = async (model: Model, training: Training) => {
    onClose();
    if (model.datasetId) {
      await startTraining(training.id);
      history.push(`/custom/${model.algorithm.id}/model/${model.id}`);
    } else {
      history.push(`/custom/${training.id}/result?readOnly=false&createModel=true`);
    }
  };

  const onClickUrl = useCallback(
    (record: Model) => {
      return `/custom/${algorithmId}/model/${record.id}`;
    },
    [algorithmId]
  );

  const onChange = async (data: Model, status: boolean, info: any) => {
    await updateMultiModel(data.id, status, info);
    refetch();
  };

  const onDelete = async (record: Model) => {
    await deleteModel(record.id);
    refetch();
  };

  const onRename = async (record: Model) => {
    setRenameModel(record);
  };

  const onCloseRename = () => {
    setRenameModel(null);
    refetch();
  };

  const onFilterChange = (event: SyntheticEvent<HTMLInputElement>) => {
    setFilter(event.currentTarget.value);
  };

  const isClassification = isDocumentClassification(algorithm);
  const isMultiflowAlgorithm = algorithm && isMultiflow(algorithm);

  useEffect(() => {
    setPaginationObject(defaultPaging);
  }, [algorithmId]);

  return (
    <>
      <PageHeader
        container="Customisation"
        header={algorithm ? getAlgorithmValue(algorithm.name) || null : null}
        description={
          algorithm &&
          (algorithmMenuDescriptions.customization[algorithm.type] || algorithmMenuDescriptions.customization.other)
        }
      />
      <Row className="items-center my-3">
        <Col span={12}>
          <Input placeholder="Filter" onChange={onFilterChange} allowClear={true} />
        </Col>
        <Col span={12} className="flex justify-end">
          <Button type="primary" onClick={onCreateModel} size={'large'}>
            New model
          </Button>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {error ? (
            <ErrorText>Something went wrong. We could not find the models you were looking for.</ErrorText>
          ) : (
            <>
              <Table
                className="ant-table-toolbox"
                columns={GetModelOverviewTableColumns(onClickUrl, onDelete, onChange, onRename, isMultiflowAlgorithm)}
                rowKey={record => record.id}
                dataSource={models}
                loading={loading}
                pagination={false}
              />
              <Row className="flex justify-end items-center my-4">
                <Col className="mr-4">{totalItemCount} models</Col>
                <Paging
                  showSizeChanger={false}
                  defaultCurrent={1}
                  current={paginationObject.page}
                  pageSize={defaultPaging.pageSize}
                  total={totalItemCount}
                  onChange={page => {
                    setPaginationObject({ ...paginationObject, page: page });
                  }}
                />
              </Row>
            </>
          )}
        </Col>
      </Row>
      {isClassification ? (
        <CreateClassificationModel
          algorithmId={algorithmId || ''}
          visible={createModel}
          onClose={onClose}
          onCreate={onCreate}
        />
      ) : algorithm && isMultiflowAlgorithm ? (
        <CreateMultiModel algorithmId={algorithmId} visible={createModel} onClose={onClose} onCreate={onCreate} />
      ) : (
        <CreateModel algorithmId={algorithmId || ''} visible={createModel} onClose={onClose} onCreate={onCreate} />
      )}
      {algorithm && renameModel && (
        <>
          {!isMultiflowAlgorithm ? (
            <RenameModel
              visible={true}
              onClose={onCloseRename}
              onCancel={() => {
                setRenameModel(null);
              }}
              model={renameModel}
            />
          ) : (
            <RenameMultiModel
              visible={true}
              onClose={onCloseRename}
              onCancel={() => {
                setRenameModel(null);
              }}
              model={renameModel}
            />
          )}
        </>
      )}
    </>
  );
};

export default ModelOverview;
